import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Tabs, Space, message, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, EyeOutlined, CopyOutlined, DeleteOutlined, AppstoreOutlined, GlobalOutlined, LinkOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { scenarioAPI, loiAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const STATUS_CONFIG = {
  draft: { color: 'default', label: '草稿' },
  active: { color: 'green', label: '啟用中' },
  archived: { color: 'orange', label: '已封存' },
};

const ScenarioListPage = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [form] = Form.useForm();

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await scenarioAPI.list(params);
      setScenarios(res.data || []);
    } catch (error) {
      message.error('載入情境列表失敗：' + error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await loiAPI.getCampaigns();
      setCampaigns(res.data || []);
    } catch (error) {
      console.log('載入活動列表失敗:', error.message);
    }
  }, []);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreateLoading(true);
      const res = await scenarioAPI.create(values);
      message.success('情境建立成功');
      setCreateModalVisible(false);
      form.resetFields();
      fetchScenarios();
      if (res.data && res.data.id) {
        navigate(`/scenarios/${res.data.id}`);
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('建立情境失敗：' + error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleClone = async (id) => {
    try {
      await scenarioAPI.clone(id, {});
      message.success('情境複製成功');
      fetchScenarios();
    } catch (error) {
      message.error('複製情境失敗：' + error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await scenarioAPI.delete(id);
      message.success('情境已刪除');
      fetchScenarios();
    } catch (error) {
      message.error('刪除情境失敗：' + error.message);
    }
  };

  const columns = [
    {
      title: '情境名稱',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => navigate(`/scenarios/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = STATUS_CONFIG[status] || { color: 'default', label: status };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '步驟數',
      dataIndex: 'steps_count',
      key: 'steps_count',
      width: 80,
      align: 'center',
      render: (count) => count || 0,
    },
    {
      title: '行銷網站',
      dataIndex: 'marketing_site_slug',
      key: 'site',
      width: 220,
      render: (slug, record) => {
        if (!slug) return <span style={{ color: '#999' }}>未部署</span>;
        const siteUrl = `${window.location.origin}/site/${slug}`;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={record.marketing_site_published ? 'green' : 'orange'} icon={<GlobalOutlined />}>
              {record.marketing_site_published ? '已發佈' : '草稿'}
            </Tag>
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              <LinkOutlined style={{ marginRight: 4 }} />{siteUrl}
            </a>
          </Space>
        );
      },
    },
    {
      title: '關聯活動',
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      render: (name) => name || <span style={{ color: '#999' }}>未關聯</span>,
    },
    {
      title: '建立者',
      key: 'creator',
      width: 160,
      render: (_, record) => {
        if (!record.created_by) return <span style={{ color: '#999' }}>-</span>;
        return (
          <div>
            <a onClick={() => navigate(`/customers/${record.created_by}`)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <UserOutlined />
              <span>{record.creator_name || `用戶 #${record.created_by}`}</span>
            </a>
            {record.creator_company && (
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{record.creator_company}</div>
            )}
          </div>
        );
      },
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString('zh-TW') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space>
          <Tooltip title="檢視/編輯">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/scenarios/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="銷售網站">
            <Button
              type="link"
              icon={<ShopOutlined />}
              onClick={() => navigate(`/scenarios/${record.id}/site`)}
            />
          </Tooltip>
          {record.created_by && (
            <Tooltip title="客戶後台管理">
              <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => navigate(`/customers/${record.created_by}`)}
                style={{ color: '#722ed1' }}
              />
            </Tooltip>
          )}
          <Tooltip title="複製">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleClone(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="確定要刪除此情境嗎？"
            onConfirm={() => handleDelete(record.id)}
            okText="確定"
            cancelText="取消"
          >
            <Tooltip title="刪除">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    { key: 'all', label: '全部' },
    { key: 'draft', label: '草稿' },
    { key: 'active', label: '啟用中' },
    { key: 'archived', label: '已封存' },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            <span>行銷情境管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            新增情境
          </Button>
        }
      >
        <Tabs
          activeKey={statusFilter}
          onChange={setStatusFilter}
          items={tabItems}
          style={{ marginBottom: 16 }}
        />
        <Table
          columns={columns}
          dataSource={scenarios}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: '尚無情境資料' }}
        />
      </Card>

      <Modal
        title="新增行銷情境"
        open={createModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={createLoading}
        okText="建立"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="情境名稱"
            rules={[{ required: true, message: '請輸入情境名稱' }]}
          >
            <Input placeholder="例如：Q2 經銷商招募流程" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="情境說明（選填）" />
          </Form.Item>
          <Form.Item name="campaign_id" label="關聯活動">
            <Select placeholder="選擇活動（選填）" allowClear>
              {campaigns.map((c) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScenarioListPage;
